import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { debounceTime, distinctUntilChanged, skip, tap } from 'rxjs/operators';
import { fromEvent } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../../../../core/reducers';
import {
  LayoutUtilsService,
  MessageType,
  QueryParamsModel,
} from '../../../../../../core/_base/crud';
import { TranslateService } from '@ngx-translate/core';
import { AuthService, UserService } from 'src/app/core/auth/_services';
import { MeetingsDataSource } from 'src/app/core/timeline/_data-sources/meeting.datasource';
import { Meeting, TaskService } from 'src/app/core/timeline';
import { selectMeetingsPageLastQuery } from 'src/app/core/timeline/_selectors/meeting.selectors';
import { MeetingsPageRequested } from 'src/app/core/timeline/_actions/meeting.actions';
import { MeetingViewDialogComponent } from '../meeting-view/meeting-view-dialog.component';
import { MeetingEditComponent } from '../meeting-edit/meeting-edit.component';
import { FollowUpDialogComponent } from '../../../../follow-up/follow-up-dialog/follow-up-dialog.component';
import { clone } from 'lodash-es';
import { StringUtilsService } from '../../../../../../core/services/string-utils.service';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { GeneralAutocompleteComponent } from '../../../../../partials/layout/general-autocomplete/general-autocomplete.component';
import { ContactRelationshipsService } from '../../../../../../core/lists';
import { MatButton } from '@angular/material/button';
import { DatePipe } from '@angular/common';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'kt-meetings-list',
  templateUrl: './meetings-list.component.html',
  styleUrls: ['./meetings-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeetingsListComponent implements OnInit, OnDestroy, AfterViewInit {
  dataSource: MeetingsDataSource;
  displayedColumns = [
    'created',
    'is_completed',
    'call_organizer',
    'from_time',
    'subject',
    'related_to_list',
    'location',
    'actions',
  ];
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild('sort1', { static: true }) sort: MatSort;
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  filterStatus = '';
  filterCondition = '';
  lastQuery: QueryParamsModel;
  selection = new SelectionModel<Meeting>(true, []);
  meetingsResult: Meeting[] = [];
  users: any[];
  submissionErrorObj: any;
  hasSubmissionErrors: boolean;
  private subscriptions: any[] = [];
  relatedIds = [];
  userFilteredIds = [];
  userObjects = [];
  userObjectNames = [];
  toDate: FormControl;
  startDate: FormControl;
  userAutocompleteCall = this.userService.searchUsers.bind(this.userService);
  entityAutocompleteCall = this.contactRelationshipsService.searchEntities.bind(
    this.contactRelationshipsService,
    { type: '' },
  );
  searchForm: FormGroup;
  userIds = '';
  @ViewChild('userAutocomplete') userAutocomplete: GeneralAutocompleteComponent;
  @ViewChild('entityAutocomplete') entityAutocomplete: GeneralAutocompleteComponent;
  size = 10;
  page = 0;
  entityIds = '';
  private user: any;
  private usersChangedFirstTime = true;
  maxDate: any;
  minDate: any;
  isMyMeetings = true;
  @ViewChild('myMeetingBtn') myMeetingBtn: MatButton;
  progressOptions = [true, false];
  currentUserId;
  /**
   * Component constructor
   *
   * @param dialog: MatDialog
   * @param stringUtils: StringUtilsService
   * @param activatedRoute: ActivatedRoute
   * @param router: Router
   * @param layoutUtilsService: LayoutUtilsService
   * @param store: Store<AppState>
   * @param cdr: ChangeDetectorRef,
   * @param translate: TranslateService,
   * @param authService: AuthService
   */
  constructor(
    public dialog: MatDialog,
    public stringUtils: StringUtilsService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private layoutUtilsService: LayoutUtilsService,
    private store: Store<AppState>,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private authService: AuthService,
    private taskService: TaskService,
    private userService: UserService,
    private fb: FormBuilder,
    private auth: AuthService,
    private contactRelationshipsService: ContactRelationshipsService,
    private datePipe: DatePipe,
  ) {
    this.auth.getUser().subscribe((user) => {
      this.user = user;
      if (user.id && user.companyID) {
        this.userIds = this.user.id;
        this.loadMeetingsList();
      }
    });
    this.startDate = new FormControl(null);
    this.toDate = new FormControl(null);
    this.maxDate = new Date();
    this.currentUserId = this.authService.currentUserId;
  }

  /**
   * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
   */

  /**
   * On init
   */
  ngOnInit() {
    // If the user changes the sort order, reset back to the first page.
    const sortSubscription = this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
    this.subscriptions.push({ id: 1, sub: sortSubscription });
    this.loadMeetingsList();

    // Filtration, bind to searchInput
    const searchSubscription = fromEvent(this.searchInput.nativeElement, 'keyup')
      .pipe(
        debounceTime(150),
        distinctUntilChanged(),
        tap(() => {
          this.paginator.pageIndex = 0;
          this.loadMeetingsList();
        }),
      )
      .subscribe();
    this.subscriptions.push({ id: 3, sub: searchSubscription });

    // Init DataSource
    this.dataSource = new MeetingsDataSource(this.store);
    const entitiesSubscription = this.dataSource.entitySubject
      .pipe(skip(1), distinctUntilChanged())
      .subscribe((res) => {
        res.forEach((ele) => {
          if (ele && ele.call_organizer) {
            this.userFilteredIds.push(ele.call_organizer);
          }
        });
      });
    this.subscriptions.push({ id: 4, sub: entitiesSubscription });
    const lastQuerySubscription = this.store
      .pipe(select(selectMeetingsPageLastQuery))
      .subscribe((res) => (this.lastQuery = res));
    // Load last query from store
    this.subscriptions.push({ id: 5, sub: lastQuerySubscription });
    this.searchForm = this.fb.group({
      entity: [null],
      user: [null],
    });
  }
  ngAfterViewInit(): void {
    if (this.user && this.userAutocomplete) {
      this.userAutocomplete.setValue(this.user);
      this.usersChangedFirstTime = false;
    }
  }

  /**
   * On Destroy
   */
  ngOnDestroy() {
    this.subscriptions.forEach((el) => {
      if (el.sub) {
        el.sub.unsubscribe();
      }
    });
  }

  /**
   * Load Meetings List
   */
  loadMeetingsList() {
    this.selection.clear();
    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction !== '' ? this.sort.direction : 'desc',
      !this.sort.active || this.sort.active === '' ? 'created' : this.sort.active,
      this.paginator.pageIndex,
      this.paginator.pageSize,
    );
    // Call request from server

    this.store.dispatch(new MeetingsPageRequested({ page: queryParams }));
    this.selection.clear();
    setTimeout(() => {
      this.loadOrganizers();
    }, 1000);
  }
  loadOrganizers() {
    if (this.userFilteredIds.length > 0) {
      this.taskService.getUsersbyId(this.userFilteredIds).subscribe((reso) => {
        this.userObjects = [];
        this.userObjects = reso.pageOfItems;
        reso.pageOfItems.forEach((element) => {
          this.userObjectNames[element.id] = element.fullName;
        });
        this.cdr.detectChanges();
      });
    }
  }

  /**
   * Returns object for filter
   */
  filterConfiguration(): any {
    const filter: any = {};
    const searchText: string = this.searchInput.nativeElement.value;
    filter.is_completed = this.filterStatus;
    filter.searchData = searchText;
    filter.organizer_id = this.userIds;
    filter.entity_id = this.entityIds;
    filter.from_time = this.datePipe.transform(this.startDate.value, 'yyyy-MM-dd');
    filter.to_time = this.datePipe.transform(this.toDate.value, 'yyyy-MM-dd');
    return filter;
  }

  viewMeeting(meeting: Meeting) {
    const _saveMessage = this.translate.instant('DIALOG.SAVE');
    const _messageType = meeting.id ? MessageType.Update : MessageType.Create;
    const dialogRef = this.dialog.open(MeetingViewDialogComponent, {
      data: { meeting },
      width: '500px',
    });
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }
      this.layoutUtilsService.showActionNotification(
        _saveMessage,
        _messageType,
        5000,
        false,
        false,
      );
      this.loadMeetingsList();
    });
  }

  /**
   * Redirect to edit page
   *
   * @param id: any
   */
  editMeeting(id) {
    this.router.navigate(['timeline/meetings/edit/' + id]);
  }

  createNewMeeting() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.closeOnNavigation = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '75vw';
    dialogConfig.data = { mode: 'create' };
    const dialogRef = this.dialog.open(MeetingEditComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((res) => {
      if (res) this.loadMeetingsList();
    });
  }

  openFollowUpDialog(meeting) {
    const model = clone(meeting);
    model.id = meeting.id;
    this.relatedIds = [];
    meeting.related_to_list.forEach((rela) => {
      this.relatedIds.push(rela.id);
    });
    this.dialog.open(FollowUpDialogComponent, {
      data: {
        body: model,
        type: 'MEETING',
        title: meeting.subject,
        relatedIds: this.relatedIds,
      },
      width: '1200px',
    });
  }
  mapUser(user) {
    const userId: number = +user;
    const u = this.userObjects.find((e) => e.id === userId);
    if (u) {
      return u;
    }
    return;
  }
  navigateEntity(type, ent) {
    switch (type) {
      case 'FACILITY':
        this.router.navigate(['lists/facilities/edit/' + ent.id], {
          queryParams: { relativeTo: this.activatedRoute, url: '/timeline/meetings' },
        });
        return;
      case 'DOCTOR':
        this.router.navigate(['lists/doctors/edit/' + ent.id], {
          queryParams: { relativeTo: this.activatedRoute, url: '/timeline/meetings' },
        });
        return;
      case 'PATIENT':
        this.router.navigate(['lists/patients/edit/' + ent.id], {
          queryParams: { relativeTo: this.activatedRoute, url: '/timeline/meetings' },
        });
        return;
      case 'CLIENT':
        this.router.navigate(['lists/clients/edit/' + ent.id], {
          queryParams: { relativeTo: this.activatedRoute, url: '/timeline/meetings' },
        });
        return;
    }
  }
  clearFilters() {
    this.isMyMeetings = false;
    this.userIds = '';
    this.entityIds = '';
    this.filterStatus = '';
    this.userAutocomplete.setValue(null);
    this.entityAutocomplete.setValue(null);
    this.clearDateFilter();
    this.resetPagination();
    this.loadMeetingsList();
  }
  setMinDate($event: MatDatepickerInputEvent<any>) {
    this.minDate = $event.value;
    if (this.maxDate && this.minDate) {
      this.resetPagination();
      this.loadMeetingsList();
    }
  }
  setMaxDate($event: MatDatepickerInputEvent<any>) {
    this.maxDate = $event.value;
    if (this.maxDate) {
      this.resetPagination();
      this.loadMeetingsList();
    }
  }
  clearDateFilter() {
    if (!this.maxDate && !this.minDate) {
      return;
    }
    this.startDate.setValue(null);
    this.toDate.setValue(null);
    this.maxDate = null;
    this.minDate = null;
    this.resetPagination();
    this.loadMeetingsList();
  }
  onUsersChange(val) {
    this.resetPagination();
    if (this.usersChangedFirstTime) {
      this.usersChangedFirstTime = false;
      return;
    }
    if (!val || !val.id) {
      this.userIds = '';
      this.isMyMeetings = false;
      this.loadMeetingsList();
      return;
    }
    if (val && val.id) {
      if (val.id === this.user.id) {
        this.isMyMeetings = true;
      }
      this.userIds = val.id;
      this.loadMeetingsList();
      return;
    }
  }
  onEntityChange(val) {
    this.entityIds = val ? val.id : '';
    if (!val) {
      this.entityIds = '';
    }
    this.resetPagination();
    this.loadMeetingsList();
  }
  resetPagination() {
    this.page = 0;
    this.size = 10;
    this.paginator.pageIndex = 0;
    this.paginator.pageSize = 10;
  }
  loadPage($event: PageEvent) {
    this.page = $event.pageIndex;
    this.size = $event.pageSize;
    this.loadMeetingsList();
  }
  fetchMyMeetings() {
    this.isMyMeetings = !this.isMyMeetings;
    if (this.isMyMeetings) {
      this.userIds = '' + this.user.id;
      this.userAutocomplete.setValue(this.user);
    } else {
      this.userIds = '';
      this.userAutocomplete.setValue(null);
    }
    this.loadMeetingsList();
  }
  updateSalesCall(salesCall, call_organizer) {
    const permissions = JSON.parse(localStorage.getItem('currentUserPermissions'));
    const canEditCompletedSalesCalls = permissions.canEditCompletedSalesCalls;
    const canEditOtherResponsibleSalesCalls = permissions.canEditOtherResponsiblesSalesCalls;
    if (!canEditCompletedSalesCalls && salesCall.is_completed) {
      this.layoutUtilsService.showActionNotification(
        'You do not have permission to update sales calls that are already completed',
        MessageType.Update,
        3000,
        false,
        false,
      );
      return;
    }
    const isResponsible = salesCall.receivers.filter(
      (val) => val.id === this.currentUserId && val.type === 'RESPONSIBLE_PERSON',
    );
    if (!canEditOtherResponsibleSalesCalls && isResponsible.length === 0) {
      this.layoutUtilsService.showActionNotification(
        'You do not have permission to update sales calls that are responsibility of other team members',
        MessageType.Update,
        3000,
        false,
        false,
      );
      return;
    }
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.closeOnNavigation = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '75vw';
    dialogConfig.data = { salesCall, call_organizer, mode: 'update' };
    const dialogRef = this.dialog.open(MeetingEditComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((res) => {
      if (res) this.loadMeetingsList();
    });
  }
}
